import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { 
  Shield, 
  Heart, 
  Scale, 
  Rocket, 
  BarChart3, 
  Handshake, 
  Users, 
  Lightbulb, 
  Clock,
  Award,
  CheckCircle
} from 'lucide-react';

const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg";

const coreValues = [
  { icon: Award, title: 'Scientific Integrity', description: 'Rigorous standards in all research' },
  { icon: Heart, title: 'Patient-Centered', description: 'Prioritizing patient safety' },
  { icon: Scale, title: 'Regulatory Compliance', description: 'Global regulatory adherence' },
  { icon: Rocket, title: 'Medical Breakthroughs', description: 'Faster paths to market' },
  { icon: BarChart3, title: 'Data-Driven', description: 'Analytics for optimal outcomes' },
  { icon: Handshake, title: 'Partnership', description: 'Extension of your team' },
  { icon: Users, title: 'Diversity & Inclusion', description: 'Representative patient populations' },
  { icon: Lightbulb, title: 'Innovation', description: 'Technology serving clinical needs' },
  { icon: Clock, title: 'Reliability', description: 'Consistent delivery on timelines' },
  { icon: Shield, title: 'Veteran Dedication', description: 'Military discipline applied' }
];

// const milestones = [
//   { year: '2018', title: 'Company Founded', description: 'Established by veteran leaders' },
//   { year: '2019', title: 'First Partnership', description: 'Top pharma company partnerships' },
//   { year: '2021', title: 'Sclin EDC Launch', description: 'Proprietary EDC platform' },
//   { year: '2023', title: 'DocuDefender AI', description: 'AI-powered eTMF system' },
//   { year: '2024', title: 'Global Expansion', description: '15+ countries, 500+ trials' }
// ];

export const About = ({ onDemoClick }) => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  React.useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`https://vetforcesolutions.com/backend/api/sliders/our_team`);
        console.log('Fetched jobs data:', response.data.data);
        // Ensure jobs is always an array
        const jobsData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data?.data || []; 
        setJobs(jobsData);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobs([]); // Set to empty array on error
      } finally {
        setLoading(false);
      }
    };
    const fetchMilestones = async () => {
      try {
        const response = await axios.get(`https://vetforcesolutions.com/backend/api/sliders/milestones`);
        console.log('Fetched jobs data:', response.data.data);
        // Ensure jobs is always an array
        const jobsData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data?.data || []; 
        setMilestones(jobsData);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setMilestones([]); // Set to empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
    fetchMilestones();
  }, []);


  return (
    <div className="min-h-screen bg-[#090B17] pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="section-label">About VetForce Solutions</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="about-title">
                Veteran-Led Excellence in <span className="text-gradient">Clinical Research</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg mb-6 leading-relaxed">
                At VetForce Solutions, we integrate the precision, discipline, and problem-solving 
                expertise of a veteran-led team with the rigor of clinical research to drive 
                medical advancements.
              </p>
              <p className="text-[#9F999F] leading-relaxed">
                Our comprehensive, end-to-end clinical trial solutions ensure regulatory excellence, 
                patient safety, and data integrity, accelerating the development of groundbreaking 
                therapies for pharmaceutical, biotech, and medical device companies worldwide.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden glow-subtle">
                <img 
                  src={TEAM_IMAGE} 
                  alt="VetForce Team" 
                  className="w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certifications & Accreditations - Scrolling Strip */}
      <section className="section-surface border-y border-[#2E3446]">
        <div className="marquee-container">
          <div className="marquee-content">
            {[
              { label: 'FDA Compliance Experts' },
              { label: 'ICH-GCP Certified' },
              { label: 'Global Research Network' },
              { label: 'AI-Powered Analytics' },
              { label: '21 CFR Part 11 Compliant' },
              { label: 'Veteran-Led Organization' },
              { label: '24/7 Support' },
              { label: 'Real-Time Insights' },
              { label: 'FDA Compliance Experts' },
              { label: 'ICH-GCP Certified' },
              { label: 'Global Research Network' },
              { label: 'AI-Powered Analytics' },
              { label: '21 CFR Part 11 Compliant' },
              { label: 'Veteran-Led Organization' },
            ].map((item, index) => (
              <div key={index} className="trust-badge">
                <Shield className="w-5 h-5" />
                <span className="font-medium text-white">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Identification & Certifications Row */}
      <section className="bg-[#0D1020] border-b border-[#2E3446]">
        <div className="marquee-container">
          <div className="marquee-content-slow">
            {[
              { label: 'UNIQUE ENTITY ID: VUBQWVMUCWK6' },
              { label: 'CAGE Code: 08B84' },
              { label: 'D-U-N-S: 119404620' },
              { label: 'SBA Veteran Owned' },
              { label: 'SBA Service-Disabled Veteran Owned' },
              { label: 'NVBDC Certified' },
              { label: 'NMSDC Certified' },
              { label: 'FL Department of Management Services' },
              { label: 'UNIQUE ENTITY ID: VUBQWVMUCWK6' },
              { label: 'CAGE Code: 08B84' },
              { label: 'D-U-N-S: 119404620' },
              { label: 'SBA Veteran Owned' },
              { label: 'SBA Service-Disabled Veteran Owned' },
              { label: 'NVBDC Certified' },
              { label: 'NMSDC Certified' },
              { label: 'FL Department of Management Services' },
            ].map((item, index) => (
              <div key={index} className="id-badge">
                <span className="font-medium text-[#9CA3AF]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-20 section-surface border-y border-[#2E3446] scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Our Mission</span>
              <h2 className="section-title mt-4 mb-8">
                Driving Excellence with <span className="text-gradient">Integrity</span>
              </h2>
              <p className="text-[#C9C9CD] text-lg leading-relaxed italic">
                "At VFS, our mission is to drive excellence in CRO services for pharmaceutical, 
                biotechnology, and medical device companies. Led by our disciplined veteran team, 
                we integrate AI-driven solutions to enhance efficiency and innovation, ensuring 
                success with integrity and precision."
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-12">
            <span className="section-label">Our Foundation</span>
            <h2 className="section-title mt-4">Core <span className="text-gradient">Values</span></h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="card-dark p-5 flex items-center gap-4"
              >
                <div className="icon-box flex-shrink-0">
                  <value.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-silver-shine font-semibold mb-1">{value.title}</h3>
                  <p className="text-[#9F999F] text-sm">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section id="milestones" className="py-20 section-surface border-y border-[#2E3446] scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-12">
            <span className="section-label">Our Journey</span>
            <h2 className="section-title mt-4">Company <span className="text-gradient">Milestones</span></h2>
          </div>

          <div className="max-w-2xl mx-auto">
            {milestones.map((milestone, index) => ( 
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#E1252E]/20 to-[#7F070E]/10 border border-teal-500/30 flex items-center justify-center font-mono text-sm font-bold text-[#eb0611]">
                    '{milestone.year.slice(-2)}
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="timeline-line mt-3" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <span className="text-sm font-mono text-[#eb0611]">{milestone.year}</span>
                  <h3 className="text-silver-shine font-semibold text-lg mb-1">{milestone.title}</h3>
                  <p className="text-[#9F999F] text-sm">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section
      <section id="leadership" className="py-20 scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-12">
            <span className="section-label">Leadership</span>
            <h2 className="section-title mt-4">Our <span className="text-gradient">Team</span></h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mt-4">
              Experienced professionals dedicated to advancing clinical research
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {jobs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark overflow-hidden"
              >
                <div className="h-52 flex items-center justify-center bg-gradient-to-b from-[#8F262B]/10 to-transparent">
                 
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="h-40 w-40 object-cover rounded-full border-4 border-[#8F262B]"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-silver-shine font-semibold">{item.name}</h3>
                  <p className="text-[#8F262B] text-sm mb-3">{item.department}</p>
                  <p className="text-[#9F999F] text-sm" dangerouslySetInnerHTML={{ __html: item.description }} >
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
*/}
      {/* CTA Section */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center"
          >
            <h2 className="section-title mb-4">
              Partner with <span className="text-gradient">Excellence</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mb-8">
              Join the leading pharmaceutical and biotech companies who trust VetForce Solutions
            </p>
            <button 
              onClick={onDemoClick}
              className="btn-gradient px-8 py-4"
              data-testid="about-cta-btn"
            >
              Schedule a Consultation
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
